/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import { Row, Col, Box, Button } from "@nordcloud/gnui";
import { BreadcrumbsBox } from "~/components";
import { useToggle } from "~/hooks";
import { useEstateFetch } from "~/hooks/useEstateFetchV2";
import { isEmpty, inflect } from "~/tools";
import { AttachToEnv } from "./AttachToEnv";
import { EstateRecords } from "./EstateRecords";
import { getMultiResources } from "./helpers";

export function EstatePage() {
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [showAttachmentSidebar, toggleShowAttachmentSidebar] = useToggle(false);

  const hasNoSelectedResources = isEmpty(selectedResources ?? []);

  const { result, ...filters } = useEstateFetch({});
  const { data, refetch } = result;

  const resources = useMemo(
    () => getMultiResources(selectedResources, data?.resources),
    [selectedResources, data?.resources]
  );

  const refresh = () => {
    setSelectedResources([]);
    refetch();
  };

  const estateRecordsData = useMemo(() => {
    return {
      ...result,
      data: {
        estateV2: result.data,
      },
    };
  }, [result]);

  return (
    <Row>
      <Col>
        <BreadcrumbsBox title="Estate Records">
          <Button
            disabled={hasNoSelectedResources}
            icon="link"
            onClick={toggleShowAttachmentSidebar}
          >
            {hasNoSelectedResources
              ? "Select multiple resources"
              : `Attach Selected Estate ${inflect("Record")(
                  selectedResources.length
                )}`}
          </Button>
          <AttachToEnv
            allowMultipleAssignment
            resources={resources}
            refresh={refresh}
            isOpen={showAttachmentSidebar}
            saveSelections={false}
            onClose={toggleShowAttachmentSidebar}
          />
        </BreadcrumbsBox>
        <Box spacing="spacing04">
          <EstateRecords
            allowSavingFilters
            setSelectedResources={setSelectedResources}
            selectedResources={selectedResources}
            queryFilters={filters}
            dataEstate={estateRecordsData}
          />
        </Box>
      </Col>
    </Row>
  );
}
